"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle, ChevronLeft, ChevronRight, Loader2,
  MessageSquare, RefreshCw, Search, Star, Video, X,
} from "lucide-react";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const PAGE_SIZE = 9;

const formatDate = value => {
  if (!value || Number.isNaN(Date.parse(value))) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  }).format(new Date(value));
};

const ratingOf = row => {
  const value = Number(row.rating);
  return row.rating !== null && row.rating !== "" &&
    Number.isFinite(value) && value >= 1 && value <= 5
    ? value
    : null;
};

async function fetchFeedbacks(signal) {
  if (!SUPABASE_URL || !SUPABASE_KEY) {
    throw new Error(
      "Missing NEXT_PUBLIC_SUPABASE_URL or NEXT_PUBLIC_SUPABASE_ANON_KEY. Restart Next.js after updating .env.local.",
    );
  }

  const url = new URL(
    `${SUPABASE_URL.replace(/\/$/, "")}/rest/v1/feedback_responses`,
  );

  url.searchParams.set(
    "select",
    "id,lead_id,day,full_name,phone,email,rating,comment,video_url,created_at,updated_at",
  );
  url.searchParams.set("order", "updated_at.desc,id.desc");

  const rows = [];
  let offset = 0;

  while (true) {
    const response = await fetch(url, {
      signal,
      cache: "no-store",
      headers: {
        apikey: SUPABASE_KEY,
        Accept: "application/json",
        "Range-Unit": "items",
        Range: `${offset}-${offset + 499}`,
      },
    });

    const text = await response.text();
    let data;

    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(
        `Expected JSON but received another response (${response.status}). Check the Supabase project URL.`,
      );
    }

    if (!response.ok) {
      throw new Error(data?.message || `Request failed (${response.status}).`);
    }

    if (!Array.isArray(data)) {
      throw new Error("The API returned an unexpected feedback format.");
    }

    rows.push(...data);

    const contentRange = response.headers.get("content-range");
    const totalText = contentRange?.split("/")[1];
    const total = totalText && totalText !== "*" ? Number(totalText) : null;

    offset += data.length;

    if (!data.length || (total !== null && offset >= total)) break;
  }

  return rows;
}

export default function Feedbacks() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [day, setDay] = useState(0);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);

  const refresh = useCallback(() => setRevision(value => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();

    async function load() {
      setLoading(true);
      setError("");

      try {
        const data = await fetchFeedbacks(controller.signal);
        if (!controller.signal.aborted) setRows(data);
      } catch (cause) {
        if (!controller.signal.aborted) {
          setError(cause instanceof Error ? cause.message : "Could not load feedback.");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    void load();
    return () => controller.abort();
  }, [revision]);

  useEffect(() => setPage(1), [day, query]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();

    return rows.filter(row =>
      (!day || Number(row.day) === day) &&
      (!term || [row.full_name, row.email, row.phone, row.lead_id].some(
        value => String(value ?? "").toLowerCase().includes(term),
      )),
    );
  }, [rows, day, query]);

  const counts = useMemo(
    () => [1, 2, 3, 4, 5].map(
      value => rows.filter(row => Number(row.day) === value).length,
    ),
    [rows],
  );

  const ratings = filtered.map(ratingOf).filter(value => value !== null);
  const average = ratings.length
    ? (ratings.reduce((sum, value) => sum + value, 0) / ratings.length).toFixed(1)
    : "—";

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages);
  const visibleRows = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  return (
    <main className="min-h-screen bg-purple-50/40 px-4 py-8 text-slate-900 sm:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-7 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-purple-700">
              Goa retreat
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">
              Feedback submissions
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Review attendee experiences and daily feedback.
            </p>
          </div>

          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-purple-200 bg-white px-4 py-2.5 text-sm font-medium text-purple-900 hover:bg-purple-50 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </header>

        <section className="mb-6 grid gap-3 sm:grid-cols-3">
          {[
            { label: "Matching submissions", value: filtered.length, icon: MessageSquare },
            { label: "Average rating", value: average, icon: Star },
            { label: "Video responses", value: filtered.filter(row => row.video_url).length, icon: Video },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="rounded-xl border border-purple-100 bg-white p-5">
              <div className="flex items-center justify-between text-sm text-slate-500">
                {label}
                <Icon size={18} className="text-purple-600" />
              </div>
              <p className="mt-3 text-3xl font-semibold text-purple-950">{value}</p>
            </div>
          ))}
        </section>

        <section className="mb-6 rounded-xl border border-purple-100 bg-white p-4">
          <div className="flex flex-wrap items-center gap-2">
            {[0, 1, 2, 3, 4, 5].map(value => (
              <button
                key={value}
                type="button"
                aria-pressed={day === value}
                onClick={() => setDay(value)}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                  day === value
                    ? "bg-purple-800 text-white"
                    : "bg-purple-50 text-purple-900 hover:bg-purple-100"
                }`}
              >
                {value ? `Day ${value} (${counts[value - 1]})` : `All (${rows.length})`}
              </button>
            ))}

            <div className="relative w-full lg:ml-auto lg:w-80">
              <Search size={17} className="absolute left-3 top-3 text-slate-400" />
              <input
                aria-label="Search feedback"
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Name, email, phone or lead ID"
                className="w-full rounded-lg border border-purple-100 py-2.5 pl-10 pr-10 text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100"
              />
              {query && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setQuery("")}
                  className="absolute right-3 top-3 text-red-500"
                >
                  <X size={17} />
                </button>
              )}
            </div>
          </div>
        </section>

        {error && (
          <div role="alert" className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            <AlertCircle size={18} className="shrink-0" />
            <p className="flex-1">{error}</p>
            <button type="button" disabled={loading} onClick={refresh} className="underline">
              Retry
            </button>
          </div>
        )}

        {loading && !rows.length ? (
          <div role="status" className="flex items-center justify-center gap-3 py-20 text-purple-700">
            <Loader2 className="animate-spin" />
            Loading feedback…
          </div>
        ) : !visibleRows.length ? (
          <div className="rounded-xl border border-purple-100 bg-white px-5 py-16 text-center">
            <MessageSquare size={32} className="mx-auto text-purple-300" />
            <h2 className="mt-4 font-semibold">
              {error ? "Feedback unavailable" : rows.length ? "No matching feedback" : "No feedback yet"}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {error ? "Resolve the connection error and retry." : "Submissions matching your filters will appear here."}
            </p>
          </div>
        ) : (
          <>
            <div className="grid items-start gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {visibleRows.map(row => (
                <FeedbackCard key={row.id} row={row} />
              ))}
            </div>

            <footer className="mt-6 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500">
              <p>
                Showing {(currentPage - 1) * PAGE_SIZE + 1}–
                {Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
              </p>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={currentPage <= 1}
                  onClick={() => setPage(currentPage - 1)}
                  className="rounded-lg border border-purple-200 bg-white p-2 text-purple-800 disabled:opacity-40"
                >
                  <ChevronLeft size={18} />
                </button>
                <span>{currentPage} / {pages}</span>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={currentPage >= pages}
                  onClick={() => setPage(currentPage + 1)}
                  className="rounded-lg border border-purple-200 bg-white p-2 text-purple-800 disabled:opacity-40"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </main>
  );
}

function FeedbackCard({ row }) {
  const rating = ratingOf(row);
  const resubmitted =
    Date.parse(row.updated_at) > Date.parse(row.created_at);
  const videoUrl = String(row.video_url ?? "").trim();

  return (
    <article className="overflow-hidden rounded-2xl border border-purple-100 bg-white shadow-sm">
      {videoUrl ? (
        <FeedbackVideo
          key={videoUrl}
          url={videoUrl}
          name={row.full_name || `lead ${row.lead_id}`}
        />
      ) : (
        <div className="flex aspect-video items-center justify-center gap-2 bg-purple-50 text-sm text-purple-400">
          <Video size={20} />
          No video submitted
        </div>
      )}

      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-semibold text-purple-950">
              {row.full_name || `Lead #${row.lead_id}`}
            </h2>

            <p className="mt-1 break-all text-xs text-slate-500">
              {row.email || "No email"}
            </p>

            <p className="mt-1 text-xs text-slate-500">
              {row.phone || "No phone"}
            </p>
          </div>

          <span className="shrink-0 rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-800">
            Day {row.day}
          </span>
        </div>

        <div
          className="mt-4 flex items-center gap-1"
          aria-label={
            rating === null ? "Not rated" : `Rated ${rating} out of 5`
          }
        >
          {[1, 2, 3, 4, 5].map(value => (
            <Star
              key={value}
              size={17}
              aria-hidden="true"
              className={
                rating !== null && value <= rating
                  ? "fill-amber-400 text-amber-400"
                  : "text-slate-200"
              }
            />
          ))}

          <span className="ml-2 text-sm text-slate-500">
            {rating === null ? "Not rated" : `${rating}/5`}
          </span>
        </div>

        {row.comment && (
          <p className="mt-4 whitespace-pre-wrap break-words rounded-xl bg-purple-50/70 p-3 text-sm leading-6 text-slate-700">
            {row.comment}
          </p>
        )}

        <p className="mt-4 border-t border-purple-50 pt-3 text-xs text-slate-400">
          {formatDate(row.updated_at || row.created_at)} IST
          {resubmitted && " · Resubmitted"}
        </p>
      </div>
    </article>
  );
}

function FeedbackVideo({ url, name }) {
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  function handleError(event) {
    const code = event.currentTarget.error?.code;

    const messages = {
      1: "Video loading was interrupted.",
      2: "Could not download the video. Check your connection or retry.",
      3: "The browser could not decode this recording.",
      4: "This video format is unsupported or the video is unavailable.",
    };

    setError(messages[code] || "Could not load the video.");
  }

  return (
    <div className="bg-purple-950">
      <video
        key={`${url}-${attempt}`}
        src={url}
        controls
        playsInline
        preload="metadata"
        aria-label={`Feedback video from ${name}`}
        onLoadedData={() => setError("")}
        onError={handleError}
        className="aspect-video w-full bg-purple-950 object-contain"
      />

      {error && (
        <div
          role="alert"
          className="border-t border-purple-800 p-4 text-sm text-purple-100"
        >
          <p>{error}</p>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => {
                setError("");
                setAttempt(value => value + 1);
              }}
              className="rounded-lg bg-purple-800 px-3 py-2 font-medium text-white hover:bg-purple-700"
            >
              Retry video
            </button>

            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-lg border border-purple-700 px-3 py-2 font-medium text-white hover:bg-purple-900"
            >
              Open original video
            </a>
          </div>
        </div>
      )}
    </div>
  );
}